import {Component, OnInit, ViewChild} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {Validators, UntypedFormGroup, UntypedFormBuilder} from '@angular/forms';
import {Router, ActivatedRoute} from '@angular/router';
import {SocialAuthService, SocialUser} from '@abacritt/angularx-social-login';
import {FacebookLoginProvider, GoogleLoginProvider} from '@abacritt/angularx-social-login';
import {catchError, filter, map, switchMap, switchMapTo, take, tap} from 'rxjs/operators';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';
import {AnalyticsServiceNameModel, TelemetryService} from 'telemetry-library';

import HTTP_STATUS_CODES from 'src/app/shared/enums/HttpStatusCodesEnum';
import {TerraUtils} from 'src/app/shared/TerraUtils';
import {UserService} from 'src/app/services/shared/user.service';
import {RoutingService} from 'src/app/services/routing.service';
import {UserType} from 'src/app/account/login/UserType.enum';
import {LoginRequest} from 'src/app/account/login/login-request.model';
import {AgentDetailsResponse} from 'src/app/account/my-account/model/agentDetailsResponse.model';
import {ForbiddenErrorMessageOptions} from 'src/app/account/login/ForbiddenUserError.enum';
import {AppType} from 'src/app/shared/enums/AppType.enum';
import {AppSettingsService} from 'src/app/services/app-settings.service';
import {CustomValidators} from 'src/app/shared/validators/custom.validators';
import {ReCaptcha2Component} from 'ngx-captcha';
import {LoggerService} from 'src/app/shared/errors/logger.service';
import {EMPTY, defer, from} from 'rxjs';
import {LpUserDataService} from 'src/app/services/lp/lp-user-data.service';

@Component({
  selector: 'terra-login-lp',
  templateUrl: './login-lp.component.html',
  styleUrls: ['./login-lp.component.scss']
})
export class LoginLpComponent extends OnDestroyMixin implements OnInit {
  // consts
  paths = TerraUtils.consts.paths;
  DEFAULT_LP_LOGGED_IN_INDEX = this.routingService.lpHomePage;
  EMAIL_VERIFICATION = this.paths.EMAIL_VERIFICATION;
  CURRENT_LOGIN_PAGE = this.routingService.loginLpPage;
  gpUrl = this.appSettingsService.gpFrontEnvironmentAppBaseUrl;
  CAPTCHA_INVALID = 3;

  externalProviderIds = {
    google: GoogleLoginProvider.PROVIDER_ID,
    facebook: FacebookLoginProvider.PROVIDER_ID
  };

  loginForm: UntypedFormGroup;
  isLoading = false;
  showPassword = false;

  ssoLoader$ = this.authService.initState;
  referrerLogo$ = this.userService.lp_referrerLogo$.pipe(
    untilComponentDestroyed(this)
  );

  emailSessionToken: string = null;

  authenticateTwoFactor = false;

  loginErrors = {
    // All properties of the loginErrors object should be cleared in the clearErrors() function
    suspended: false,
    agentUserErrorVisible: false,
    credentialsErrorVisible: false,
    message: null,
    captcha: false
  };

  obtainingAccessToken = false;

  @ViewChild('recaptcha') recaptcha: ReCaptcha2Component;
  userFromState: string;
  passFromState: string;

  constructor(
    private fb: UntypedFormBuilder,
    private userService: UserService,
    private lpUserService: LpUserDataService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: SocialAuthService,
    public routingService: RoutingService,
    private titleService: Title,
    public appSettingsService: AppSettingsService,
    private telemetryService: TelemetryService,
    private logger: LoggerService) {
    super();
    this.userFromState = this.router.getCurrentNavigation()?.extras?.state?.usr;
    this.passFromState = this.router.getCurrentNavigation()?.extras?.state?.pass;
  }

  ngOnInit() {
    this.emailSessionToken = this.route.snapshot.queryParams.emailSessionToken;
    if (!this.emailSessionToken) {
      const returnUrl = this.routingService.getReturnUrlFromCookie;
      const urlParams = returnUrl && returnUrl.indexOf('?') > -1 ? new URLSearchParams(returnUrl.substring(returnUrl.indexOf('?'))) : null;
      this.emailSessionToken = urlParams && urlParams.get('emailSessionToken');
    }

    this.titleService.setTitle('Login' + TerraUtils.consts.LpPageTitleSuffix);

    this.loginForm = this.fb.group({
      username: [this.userFromState, [Validators.required, CustomValidators.EmailWithSpaces]],
      password: [this.passFromState, [Validators.required]],
      recaptcha: ['', [CustomValidators.requiredIf(() => 
        !this.appSettingsService.isTestingEnv && !this.appSettingsService.isAutomationEnv)]]
    });

    const email = this.route.snapshot.queryParamMap.get('email');
    if (email) {
      this.loginForm.get('username').setValue(decodeURIComponent(email));
    }

    this.authService.authState.pipe(untilComponentDestroyed(this),
      filter(socialUser => !!socialUser),
      tap(socialUser => this.handleUserLoggedInWithSso(socialUser)),
      catchError(err => {
        if (err === 'User cancelled login or did not fully authorize.' || err?.error === 'popup_closed_by_user') {
          return EMPTY;
        }
        this.logger.error(`LP side -> signInWithExternalProvider`, err);
        return EMPTY;
      })).subscribe((user) => {
        console.log(user);
      });
  }

  login() {
    this.clearErrors();
    if (this.loginForm.valid) {
      this.isLoading = true;
      this.showPassword = false;

      const loginRequest = new LoginRequest(this.loginForm.value.username, this.loginForm.value.password);
      loginRequest.appType = AppType.LpApp;
      loginRequest.recaptchaToken = this.loginForm.value.recaptcha;

      if (this.emailSessionToken !== null) {
        loginRequest.emailSessionToken = this.emailSessionToken;
      }

      this.userService.login(loginRequest)
        .subscribe({
          next: response => {
            const clientDetails = response.clientDetails;
            this.telemetryService.create({
              eventID: '1',
              eventTitle: 'LOG IN LP',
              additional: {
                isMobileDevice: TerraUtils.isMobileDevice()
              }
            }, AnalyticsServiceNameModel.Mixpanel | AnalyticsServiceNameModel.Insights);

            this.redirectAfterLogin(clientDetails);
          },
          error: (error) => {
            this.onError(error);
          }
        });

    } else {
      this.loginErrors.captcha = true;
    }
  }

  signInWithExternalProvider(ssoProviderId: string): void {
    if (this.obtainingAccessToken || this.isLoading) {
      return;
    }

    this.authService.initState.pipe(
      filter(isReady => isReady),
      untilComponentDestroyed(this),
      take(1),
      switchMapTo(
        defer(() => this.authService.signIn(ssoProviderId)).pipe(take(1))
      )
    ).subscribe(
      ssoUser => {
        if (ssoUser) {
          this.handleUserLoggedInWithSso(ssoUser);
        }
      },
      error => {
        if (error === 'User cancelled login or did not fully authorize.' || error?.error === 'popup_closed_by_user') {
          return null;
        }
        this.logger.error(`LP side -> signInWithExternalProvider`, error);
        return null;
      });
  }

  private handleUserLoggedInWithSso(socialUser: SocialUser) {
    const loginRequest = new LoginRequest(socialUser.email, null, socialUser.authToken ?? socialUser.idToken, socialUser.provider, UserType.Client);
    loginRequest.appType = AppType.LpApp;
    this.obtainLocalAccessToken(loginRequest);
  }

  private clearErrors() {
    this.loginErrors.suspended = false;
    this.loginErrors.agentUserErrorVisible = false;
    this.loginErrors.credentialsErrorVisible = false;
    this.loginErrors.message = null;
    this.loginErrors.captcha = false;
  }

  private redirectAfterLogin(clientDetails: AgentDetailsResponse) {

    this.userService.gp_referrerFromQuery$.pipe(
      take(1),
      filter(referrerFromUrl => !referrerFromUrl),
      switchMap(_ => this.userService.getLogoByReferrer())
    )
      .subscribe(
        referrer => {
          if (!!referrer) {
            this.userService.setReferrerInCookie(this.appSettingsService.gpReferrerCookieKey, referrer);
          }
        }
      );

    const returnUrl = this.routingService.getReturnUrlFromCookie;
    const isSamePage = returnUrl && returnUrl.indexOf(this.CURRENT_LOGIN_PAGE) === 0;

    if (clientDetails.mustVerifyEmail) {
      this.router.navigateByUrl(this.paths.EMAIL_VERIFICATION);
      return;
    }

    if (clientDetails.isLpOnboardingRequired && !clientDetails.isLpOnboardingFilled) {
      this.router.navigateByUrl(this.routingService.lpOnboardingPagePersonalDetails);
      return;
    }

    // if a return url exists, navigate to it
    if (!isSamePage && returnUrl && returnUrl !== TerraUtils.consts.paths.LOGOUT) {
      this.routingService.goToReturnUrl();
      return;
    }

    // navigate to default page
    this.router.navigate([this.DEFAULT_LP_LOGGED_IN_INDEX]);
  }

  private obtainLocalAccessToken(loginModel: LoginRequest) {
    if (this.obtainingAccessToken || this.isLoading) {
      return;
    }
    this.obtainingAccessToken = true;
    this.isLoading = true;

    this.userService.obtainLocalAccessToken(loginModel)
      .pipe(
        map(response => {
          this.isLoading = false;
          return response;
        })
      )
      .subscribe(result => {
        const clientDetails = result.clientDetails;
        this.redirectAfterLogin(clientDetails);
      },
        error => {
          this.obtainingAccessToken = false;
          this.onError(error);
        }
      );
  }

  private onError(err: any) {
    this.loginForm.get('password').setValue('');
    this.recaptcha && this.recaptcha.resetCaptcha();

    switch (err.status) {
      // We recognize the user, but he is not allowed access to this system (Terra  of Admin user)
      case HTTP_STATUS_CODES.FORBIDDEN:
        if (err && err.error) {
          const errMessage = err.error.message;
          if (errMessage === ForbiddenErrorMessageOptions.Suspended) {
            this.loginErrors.suspended = true;
            break;
          } else if (errMessage === ForbiddenErrorMessageOptions.CredentialsError) {
            this.loginErrors.credentialsErrorVisible = true;
            break;
          } else if (errMessage === ForbiddenErrorMessageOptions.Agent) {
            this.loginErrors.credentialsErrorVisible = true;
            break;
          }
        }
        this.loginErrors.message = 'An error has occurred. Please contact support.';
        break;
      // User credentials are incorrect
      case HTTP_STATUS_CODES.UNAUTHORIZED:
        this.loginErrors.credentialsErrorVisible = true;
        break;
      case HTTP_STATUS_CODES.BAD_REQUEST:
        if (err.error === this.CAPTCHA_INVALID) {
          this.loginErrors.credentialsErrorVisible = true;
        }
        break;
      // Any other server error
      case HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR:
        this.loginErrors.message = 'An error has occurred. Please try again, and if no luck, please contact support.';
        break;
      default:

        if (!err) {
          this.loginErrors.credentialsErrorVisible = true;
        } else {
          this.loginErrors.message = 'An error has occurred. Please try again, and if no luck, please contact support.';
          console.log(err);
        }
    }
    this.isLoading = false;
  }
}
